import { admin2 } from './instance'

/**
 * 獲取權限清單
  * @param {Object} params
  * @param {string} params.shopId - 商店 ID（path）
  * @returns {Promise<import('axios').AxiosResponse>} 權限清單的 axios 回應
 */
export const GetPermissionList = async ({
  shopId,
}) => {
  return await admin2({
    url: `/${shopId}/permissionGate/list`,
  })
}

/**
 * 檢查權限
 * @param {Object} params
 * @param {string} params.shopId - 商店 ID（path）
 * @param {string} params.featureKey - 權限鍵值（query）
 * @returns {Promise<import('axios').AxiosResponse>} 權限檢查結果的 axios 回應
 */
export const CheckPermissions = async ({ shopId, featureKey }) => {
  return await admin2({
    url: `/${shopId}/permissionGate/check`,
    params: {
      featureKey,
    },
  })
}

/**
 * 獲取權限資訊
 * @param {string} featureKey - 權限鍵值
 * @returns {Promise} 權限詳細資訊
 */
/**
 * Fetch information about a feature key for a specific shop.
 *
 * @async
 * @function GetFeatureKeyInfo
 * @param {Object} params - Function parameters.
 * @param {string|number} params.shopId - Shop identifier used in the request path.
 * @param {string} params.featureKey - The feature key to retrieve information for.
 * @returns {Promise<any>} Resolves with the API response containing feature key information.
 * @throws {Error} If the network request fails or the API returns an error.
 */
export const GetFeatureKeyInfo = async ({ shopId, featureKey }) => {
  return await admin2({
    url: `/${shopId}/permissionGate/featureKeyInfo`,
    params: {
      featureKey,
    },
  })
}

/*
interface FeatureAccess {
  type: 'FeatureKey';
  key: string;
  effect: 'allow' | 'deny';
}

interface PermissionGateAccess {
  type: 'PermissionGate';
  permissionGate: PermissionGate;
}

type Access = FeatureAccess | PermissionGateAccess;

interface PermissionGate {
  type: 'AnyName';
  name: string;
  hasAccess: Access[];
}
*/

export class PermissionGate {
  constructor (
    name,
    type,
    hasAccess,
  ) {
    this.name = name
    this.type = type
    this.hasAccess = hasAccess
  }

  /**
   * 檢查某權限是否可用、規則來源
   * @param {string} featureKey 權限名稱
   * @param {array} historyPermissionGate 檢查歷史
   *
   * @return {boolean} 是否可用
   * @return {PermissionGate[]} 規則來源由近到遠
   */
  check (featureKey, historyPermissionGate = []) {
    let matchAccess = null
    for (let i = this.hasAccess.length - 1; i >= 0; i--) {
      const access = this.hasAccess[i]
      if (access.type === 'FeatureKey') {
        if (access.key === featureKey) {
          matchAccess = access
          break
        }
        const row = featureKey.split('.')
        while (row.length > 0) {
          row.pop()
          if ([...row, '*'].join('.') === access.key) {
            matchAccess = access
            break
          }
        }
      } else if (access.type === 'PermissionGate') {
        const [result, nextHistoryPermissionGate] = access.permissionGate.check(featureKey)
        if (result !== null) {
          return [
            result,
            [...historyPermissionGate, this, ...nextHistoryPermissionGate],
          ]
        }
      }
    }

    if (!matchAccess) {
      return [
        null,
        historyPermissionGate,
      ]
    }
    if (matchAccess.effect === 'allow') {
      return [
        true,
        [...historyPermissionGate, this],
      ]
    } else if (matchAccess.effect === 'deny') {
      return [
        false,
        [...historyPermissionGate, this],
      ]
    }

    return [
      null,
      historyPermissionGate,
    ]
  }

  serialize () {
    return {
      name: this.name,
      type: this.type,
      hasAccess: this.hasAccess.map((access) => {
        if (access.type === 'FeatureKey') {
          return {
            type: 'FeatureKey',
            key: access.key,
            effect: access.effect,
          }
        } else if (access.type === 'PermissionGate') {
          return {
            type: 'PermissionGate',
            permissionGate: access.permissionGate.serialize(),
          }
        }
        return null
      }),
    }
  }

  static deserialize (data) {
    return new PermissionGate(
      data.name,
      data.type,
      data.hasAccess.map((access) => {
        if (access.type === 'FeatureKey') {
          return {
            type: 'FeatureKey',
            key: access.key,
            effect: access.effect,
          }
        } else if (access.type === 'PermissionGate') {
          return {
            type: 'PermissionGate',
            permissionGate: PermissionGate.deserialize(access.permissionGate),
          }
        }
        return null
      }),
    )
  }

  /**
   * 轉換為 featureList（新定義支援舊定義）
   *
   * @return {object} featureList
   */
  toFeatureList () {
    return this.hasAccess.reduce((acc, access) => {
      if (access.type === 'FeatureKey') {
        acc = {
          ...acc,
          [access.key]: access.effect === 'allow',
        }
      } else if (access.type === 'PermissionGate') {
        acc = {
          ...acc,
          ...access.permissionGate.toFeatureList(),
        }
      }
      return acc
    }, {})
  }
}
